// Funnel-Analytics-Client.
//
// Anonymes Pre-Submit-Tracking - fired POSTs gegen
// /api/v1/funnel/{slug}/event mit einer session_id, die in sessionStorage
// lebt (stabil ueber einen Funnel-Durchlauf, frisch bei neuem Tab).
//
// Alle Funktionen sind no-op bei SSR (typeof window === 'undefined'),
// damit Server-Render nicht versucht zu tracken.

const STORAGE_KEY_SESSION = 'funnel_analytics_session_v1';

type EventType =
  | 'step_view'
  | 'step_complete'
  | 'field_focus'
  | 'abandon'
  | 'submit'
  | 'landing_view'
  | 'apply_click';

interface EventBody {
  session_id: string;
  event_type: EventType;
  step_key?: string;
  field_key?: string;
  duration_ms?: number;
  device_type?: 'desktop' | 'mobile' | 'tablet' | 'unknown';
  utm?: { source?: string; medium?: string; campaign?: string };
  extra?: string;
  job_slug?: string;
}

/** Pro Tab/Window stabile anonyme Session-ID. crypto.randomUUID() liefert
 *  einen Standard-UUID-v4 (36 chars), passt in unser 8-64-Constraint. */
export function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return '';
  try {
    const existing = sessionStorage.getItem(STORAGE_KEY_SESSION);
    if (existing && existing.length >= 8) return existing;
    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(STORAGE_KEY_SESSION, id);
    return id;
  } catch {
    // sessionStorage kann blockiert sein (Private-Tab, Quota) - dann pro Render
    // neue ID, Analytics bleibt unsauber aber crasht nicht.
    return `s_fallback_${Date.now()}`;
  }
}

/** Grobe Heuristik fuers Device-Type ohne UA-Sniffing. Touch + schmale
 *  Viewport ~ mobile; ohne Touch + breite Viewport ~ desktop. */
export function detectDeviceType():
  | 'desktop'
  | 'mobile'
  | 'tablet'
  | 'unknown' {
  if (typeof window === 'undefined') return 'unknown';
  const w = window.innerWidth || 1024;
  const touch =
    'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0;
  if (w < 700 && touch) return 'mobile';
  if (w < 1100 && touch) return 'tablet';
  return 'desktop';
}

/** UTM-Parameter aus der aktuellen URL extrahieren. */
export function readUtm(): {
  source?: string;
  medium?: string;
  campaign?: string;
} {
  if (typeof window === 'undefined') return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: { source?: string; medium?: string; campaign?: string } = {};
    const s = params.get('utm_source');
    const m = params.get('utm_medium');
    const c = params.get('utm_campaign');
    if (s) utm.source = s.slice(0, 120);
    if (m) utm.medium = m.slice(0, 120);
    if (c) utm.campaign = c.slice(0, 120);
    return utm;
  } catch {
    return {};
  }
}

/** Einen Event an das Backend schicken. Schnelles fire-and-forget via fetch
 *  mit keepalive, fallback auf sendBeacon fuer das beforeunload-Szenario. */
export function track(
  tenantSlug: string,
  event: Omit<EventBody, 'session_id'>,
  opts: { useBeacon?: boolean } = {},
): void {
  if (typeof window === 'undefined') return;
  const sessionId = getOrCreateSessionId();
  if (!sessionId) return;

  const body: EventBody = { session_id: sessionId, ...event };
  const utm = event.utm ?? readUtm();
  if (Object.keys(utm).length > 0) body.utm = utm;
  if (!body.device_type) body.device_type = detectDeviceType();

  const url = `/api/v1/funnel/${encodeURIComponent(tenantSlug)}/event`;
  const json = JSON.stringify(body);

  try {
    if (opts.useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([json], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: json,
      keepalive: true,
      credentials: 'same-origin',
    }).catch(() => {
      /* fire-and-forget - Tracking-Fehler stoeren den User nicht */
    });
  } catch {
    /* fire-and-forget */
  }
}
